"use client";

import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";
import Skeleton from "react-loading-skeleton";
import styles from "./index.module.css";

const Paystack = dynamic(() => import("../common/Paystack"), { ssr: false });

import {
  getLoggedinUser,
  normalizeName,
  useActiveCart,
  useAuth,
  useCartCancelPayment,
  useCartPayment,
  useCartSignature,
  useCheckUserMembership,
  useCloseCart,
  useFetchOrganisation,
  useGetDestinations,
  useIpDetails,
  useProcessCart,
  useUpdateCart,
} from "../../lib/util";
import toast from "react-hot-toast";
import AuthForm from "../auth/AuthForm";
import ThankYou from "../common/ThankYou";
import CartCheckout from "./cart-checkout";
import DeliveryMethod from "./delivery-method";
import FinalCheckout from "./final-checkout";
import UserInfo from "./userInfo";
import { getActiveUser, logOutUser, logTempToken } from "../slice/auth-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { updateCart } from "../slice/cart-slice";
import { useQueryClient } from "@tanstack/react-query";
import PopUp from "../product/popUp";
import { useHistoryManager } from "../history/useHistoryManager";
import { setOrganisation } from "../slice/accessOrg-slice";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";

const CartModal = ({
  cart,
  showProduct,
  modal,
  closeModal,
  accessPoint,
  activateBackButton,
  publicUser,
  setPublicUser,
  isPageMode,
  storePath,
  organisation,
  navLocation,
  userId,
  userToken,
  token,
  checkoutState,
  setCheckoutState,
  setActiveProgressCollectionId,
  startSwitch,
}) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [isMember, setIsMember] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [thankYouData, setThankYouData] = useState({});

  // popup boolean
  const [popUp, setPopUp] = useState(false);
  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const { logUserOut } = useAuth();

  const collectionId = cart?.collectionId;
  const [cartItems, setCartItems] = useState({
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  });

  useEffect(() => {
    if (cart) {
      setCartItems(cart);
    }
  }, [cart?.collectionId]);

  const { data: ipDetails, isSuccess, isError } = useIpDetails();
  const [deliveryRegion, setDeliveryRegion] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState("");
  const [confirmation, setConfirmation] = useState(false);

  const { navigateTo } = useHistoryManager(storePath);

  const invalidateCollections = () => {
    try {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: ["userCollections", userId, userToken],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["guestCollections", token],
        });
      }
    } catch (e) {
      console.log("invalidateCollections error", e);
    }
  };

  const pushCartUpdate = (cart) => {
    if (cart) {
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      setCartItems(cart);
    }
  };

  const { isLoading: isCartLoading, mutate: mutateActiveCart } = useActiveCart({
    onError: (response) => {
      setError(response.message);
      if (setActiveProgressCollectionId && collectionId) {
        setActiveProgressCollectionId(null);
      }
    },
    onSuccess: async (response) => {
      pushCartUpdate(response.data?.collection);
      invalidateCollections();
      if (setActiveProgressCollectionId && collectionId) {
        setActiveProgressCollectionId(null);
      }
    },
  });

  const {
    mutate: mutateFetchDeliveryRegion,
    isLoading: isLoadingDeliveryRegion,
  } = useGetDestinations({
    onSuccess: (res) => {
      setDeliveryRegion(res.data.destinations);
    },
    onError: (error) => {
      console.error("Error fetching :", error.message);
    },
  });

  useEffect(() => {
    if (cartItems && cartItems?.accessPointId) {
      mutateFetchDeliveryRegion({
        orgId: cartItems?.host?.profileId,
        accessPointId: cartItems?.accessPointId,
      });
    }
  }, [cartItems?.accessPointId]);

  const [paymentInfo, setPaymentInfo] = useState({
    amount: 0,
    email: "",
    reference: "",
    publicKey: "",
    transaction_charge: 0,
  });

  const { isLoadingCartSignature, mutate: mututateCartSignature } =
    useCartSignature({
      onError: (response) => {
        setError(response.message);
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);
        invalidateCollections();
        toast.success("Cart Updated");
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
    });

  const { isLoadingCartCancelPayment, mutate: mututateCartCancelPayment } =
    useCartCancelPayment({
      onError: (response) => {
        setError(response.message);
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
      onSuccess: async (response) => {
        const cart = response.data.collection;
        pushCartUpdate(response.data?.collection);
        mutateActiveCart({
          orgId: cart.host.profileId,
          accessPointId: cart.accessPointId,
        });
        invalidateCollections();
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
    });

  const { isLoading: isLoadingPaid, mutate: mutateInitializePayment } =
    useCartPayment({
      onError: (response) => {
        setCheckoutState("cartItems");
        setError(response.message);
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
      onSuccess: async (response) => {
        const {
          access,
          paymentNo,
          amountToCharge,
          transactionEmail,
          transactionCharge,
        } = response.data;
        setPaymentInfo({
          amount: amountToCharge,
          email: transactionEmail,
          reference: paymentNo,
          publicKey: access.key,
          transaction_charge: transactionCharge,
          channels: ["card"],
        });
      },
    });

  const {
    isLoading: isLoadingCheckUserMembership,
    mutate: mutateCheckUserMembership,
  } = useCheckUserMembership({
    onError: (response) => {},
    onSuccess: async (response) => {
      setIsMember(response.data.member);
    },
  });

  useEffect(() => {
    if (!organisation) return;

    if (!cart?.host?.profileId) return;

    if (organisation.orgId !== cart.host.profileId) {
      setCheckoutState("cartItems");
    }
  }, [organisation, cart?.host?.profileId]);

  const { isLoading: isLoadingProcessOrder, mutate: mutateProcessOrder } =
    useProcessCart({
      onError: (response) => {
        setCheckoutState("cartItems");
        setError(response.message);
        pushCartUpdate(response.data?.collection);
        invalidateCollections();
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);

        const cart = response.data.collection;
        const email = cart.publicOrder
          ? cart.publicInfo?.email
          : cart.author.email;

        setThankYouData({ user: { email } });

        const amountZero = Number(cart.amount || 0) === 0;

        const isInactiveInstant =
          cart.status === "INACTIVE" && cart.collectionType === "INSTANT";

        const isManualPayment =
          cart.paymentMode === "BANK_TRANSFER" ||
          cart.paymentMode === "NO_PAYMENT" ||
          cart.paymentMode === "PAY_ON_DELIVERY";

        const shouldCloseCart =
          amountZero || isInactiveInstant || isManualPayment;

        if (shouldCloseCart) {
          setCheckoutSuccess(true);
          mutateCloseCart({
            orgId: cart?.profileId,
            accessPointId: cart?.accessPointId,
          });
          invalidateCollections();
        } else {
          const orderData = {
            collectionId: cart.collectionId,
            mode: "ONLINE",
            gateWay: "PAYSTACK",
            channel: "WEB",
            amount: cart.totalAmount,
          };
          if (!cart.publicOrder && cart.userId) {
            orderData.userId = cart.userId;
          }
          mutateInitializePayment(orderData);
        }
      },
    });

  const { isLoadingUpdate, mutate: mututateUpdateCart } = useUpdateCart({
    onError: (response) => {
      setError(response.message);
      pushCartUpdate(response.data?.collection);
      invalidateCollections();
      if (setActiveProgressCollectionId && collectionId) {
        setActiveProgressCollectionId(null);
      }
    },
    onSuccess: async (response) => {
      setCheckoutState("cartItems");
      pushCartUpdate(response.data?.collection);
      setConfirmation(true);
      invalidateCollections();
      if (setActiveProgressCollectionId && collectionId) {
        setActiveProgressCollectionId(null);
      }
    },
  });

  const { isLoading: isLoadingGetCart, mutate: mutateCloseCart } = useCloseCart(
    {
      onError: (response) => {
        setError(response.message);
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);
        invalidateCollections();
        if (setActiveProgressCollectionId && collectionId) {
          setActiveProgressCollectionId(null);
        }
      },
    },
  );

  const handlePaystackSuccess = () => {
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
    setCheckoutSuccess(true);

    mutateCloseCart({
      orgId: cartItems.profileId,
      accessPointId: cartItems?.accessPointId,
    });
    invalidateCollections();
    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(null);
    }
  };

  const handlePaystackClose = () => {
    setCheckoutState("cartItems");
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
    mututateCartCancelPayment({
      collectionId: cartItems.collectionId,
    });
    invalidateCollections();
    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(null);
    }
  };

  const handleLoginClick = () => {
    setCheckoutState("authentication");
  };

  const handleLogout = async () => {
    dispatch(logOutUser());
    const geo = isSuccess ? ipDetails : null;
    const { token, tokenExpiryDate } = await logUserOut(geo);
    dispatch(
      logTempToken({
        token,
        tokenExpiryDate,
      }),
    );
    invalidateCollections();
    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(null);
    }
  };

  const handleBackToCart = () => {
    setCheckoutState("cartItems");
  };

  const handleCartLogin = (user) => {
    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(collectionId);
    }
    mututateCartSignature({
      collectionId: cartItems.collectionId,
      token: user.token,
    });
  };

  const gotoCart = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("cartItems");
  };

  const gotoDelivery = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("delivery");
  };

  const gotoContact = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("userdetails");
  };

  const handleCartSubmit = () => {
    setError("");

    // If host does not match current organisation, show inline popup over this cart
    // if (!organisation || organisation.orgId !== cart.host.profileId) {
    //   setPopUp(true);
    //   return;
    // }

    const paymentItem = cartItems.paymentItem || [];
    if (paymentItem.length === 0) {
      setError("Please select at least one item.");
      return;
    }

    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(collectionId);
    }

    if ((!loggedInUser || !loggedInUser.user) && !publicUser) {
      setCheckoutState("userdetails");
      return;
    }
    setCheckoutState("delivery");
    return;
  };

  const handlePublicUser = (user) => {
    setPublicUser(user);
    setDeliveryInfo((prev) => ({
      ...prev,
      phone: user.phone,
    }));
    setCheckoutState("delivery");
  };

  const handleDelivery = (deliveryInfo, deliveryMode) => {
    if (!loggedInUser?.user && !publicUser) {
      setError("No valid user found for order processing.");
      return;
    }

    const { deliveryRegion = "" } = deliveryInfo;
    const [region, amount] = deliveryRegion.split("-");
    const deliveryAmount = parseFloat(amount) || 0;

    const processData = {
      orgId: cartItems?.host?.profileId,
      accessPointId: cartItems?.accessPointId,
      deliveryMode,
    };

    let updatedDeliveryInfo = { ...deliveryInfo };

    switch (deliveryMode) {
      case "LOCATION":
        updatedDeliveryInfo.deliveryRegion = region;
        if (deliveryAmount) {
          processData.deliveryAmount = deliveryAmount;
        }
        break;

      case "PICKUP":
        updatedDeliveryInfo.pickUpAddress = accessPoint?.address;
        break;

      case "INSTORE":
        break;

      default:
        setError("Invalid delivery mode selected.");
        return;
    }

    setDeliveryInfo(updatedDeliveryInfo);
    processData.deliveryInfo = updatedDeliveryInfo;

    if (loggedInUser?.user) {
      processData.userId = loggedInUser.user.userId;
    } else {
      const { firstName, lastName, email, phone, intCode } = publicUser;
      processData.publicInfo = { firstName, lastName, email, phone, intCode };
    }
    setCheckoutState("processed");
    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(collectionId);
    }
    mututateUpdateCart(processData);
  };

  const handlePayment = (paymentType, documents = null) => {
    const processData = {
      orgId: cartItems?.host?.profileId,
      accessPointId: cartItems?.accessPointId,
    };

    const paymentModes = {
      ONLINE: "ONLINE",
      BANK_TRANSFER: "BANK_TRANSFER",
      NO_PAYMENT: "NO_PAYMENT",
    };

    const selectedPaymentMode = paymentModes[paymentType];

    if (!selectedPaymentMode) {
      setError(`Unsupported payment type: ${paymentType}`);
      return;
    }

    processData.paymentMode = selectedPaymentMode;

    if (paymentType === "BANK_TRANSFER" && documents) {
      processData.supportingDocuments = documents;
    }
    setCheckoutState("processed");

    if (setActiveProgressCollectionId && collectionId) {
      setActiveProgressCollectionId(collectionId);
    }

    mutateProcessOrder(processData);
  };

  // const snapshot = {
  //   storePath,
  //   organisation,
  //   accessPoint,
  //   accessPoints,
  //   accessPointId: cartItems?.accessPointId,
  //   cartItems,
  // };

  const yesClick = (cartItems) => {
    startSwitch(cartItems);
    setPopUp(false);
  };

  useEffect(() => {
    setCheckoutState("cartItems");

    if (loggedInUser && loggedInUser.user) {
      const storedUserId = Cookies.get("checkedUserId");
      if (storedUserId === loggedInUser.user.userId) {
        return;
      }
      Cookies.set("checkedUserId", loggedInUser.user.userId, { expires: 7 });
      mutateCheckUserMembership({
        userId: loggedInUser.user.userId,
        orgId: cartItems.host.profileId,
      });
    }
  }, [loggedInUser]);

  return (
    <>
      <Paystack
        paymentInfo={paymentInfo}
        onSuccessPayment={handlePaystackSuccess}
        onClosePayment={handlePaystackClose}
      />
      {checkoutSuccess ? (
        <ThankYou
          host={cartItems?.host}
          loggedInUser={loggedInUser}
          data={thankYouData}
          isMember={isMember}
        />
      ) : (
        <div
          className={`cart${
            navLocation === "marketplace" ? " marketplace" : ""
          }`}
          style={{ position: "relative" }}
        >
          {checkoutState == "cartItems" && (
            <CartCheckout
              showProduct={showProduct}
              modal={modal}
              closeModal={closeModal}
              setError={setError}
              handleLoginClick={handleLoginClick}
              handleCartSubmit={handleCartSubmit}
              cart={cartItems}
              error={error}
              cartProcessLoading={isLoadingProcessOrder || isLoadingPaid}
              confirmation={confirmation}
              activateBackButton={activateBackButton}
              onProceed={handlePayment}
              accessPoint={accessPoint}
              gotoCart={gotoCart}
              gotoDelivery={gotoDelivery}
              gotoContact={gotoContact}
              isPageMode={isPageMode}
              storePath={storePath}
              organisation={organisation}
              navLocation={navLocation}
              invalidateCollections={invalidateCollections}
              setPopUp={setPopUp}
              startSwitch={startSwitch}
            />
          )}

          {checkoutState === "authentication" && (
            <AuthForm
              onBack={handleBackToCart}
              onLogin={handleCartLogin}
              defaultLogin={false}
              activateBackButton={activateBackButton}
            />
          )}

          {checkoutState === "userdetails" && (
            <UserInfo
              onBack={handleBackToCart}
              onSuccess={handlePublicUser}
              handleLogout={handleLogout}
              publicUser={publicUser}
              activateBackButton={activateBackButton}
              setCheckoutState={setCheckoutState}
              cart={cartItems}
            />
          )}

          {checkoutState === "delivery" && (
            <DeliveryMethod
              onBack={handleBackToCart}
              accessPoint={accessPoint}
              onSuccess={handleDelivery}
              deliveryInfo={deliveryInfo}
              deliveryRegion={deliveryRegion}
              activateBackButton={activateBackButton}
              selectedMethod={selectedDeliveryMethod}
              setSelectedMethod={setSelectedDeliveryMethod}
            />
          )}

          {checkoutState === "processed" && (
            <FinalCheckout
              cartProcessLoading={
                isLoadingProcessOrder ||
                isLoadingPaid ||
                isLoadingCartCancelPayment ||
                isLoadingUpdate
              }
            />
          )}

          {popUp && (
            <PopUp
              product={cart}
              setPopup={setPopUp}
              popUp={popUp}
              yesClick={yesClick}
            />
          )}
        </div>
      )}
    </>
  );
};

export default CartModal;
